<template>
    <VTableRow>
        <VTableCell align="center">
            <span class="row-number">{{ index + 1 }}</span>
        </VTableCell>

        <VTableCell>
            <div class="id-time-cell">
                <div class="bet-id">
                    {{ bet.refNo }}
                </div>
                <div class="bet-date">
                    {{ betDate }}
                </div>
                <div class="bet-time">
                    {{ betTime }}
                </div>
            </div>
        </VTableCell>

        <VTableCell>
            <div class="selection-cell">
                <div class="contest-title">
                    {{ leagueNames }}
                </div>
                <div class="contest-subtitle">
                    {{ bet.contest.name }}
                </div>
                <div class="selections">
                    <div class="selection-label">
                        {{ i18n.t('fantasy_selection_rankings_page_selections') }}
                    </div>
                    <TeamBadge
                        v-for="selection in bet.selections"
                        v-bind:key="selection.id"
                        v-bind:team="selection"
                    />
                </div>
                <div v-if="bet.status === FantasySelectionBetStatus.Won" class="info-badges">
                    <Badge
                        v-bind:text="i18n.t('fantasy_selection_prize_pool') + ': ' + prizePoolDisplay"
                        variant="light"
                        color="brandBlue"
                        size="md"
                    />
                    <Badge
                        v-bind:text="i18n.t('fantasy_selection_ranking') + ': ' + bet.myRank + '/' + bet.pool.maxEntries"
                        variant="light"
                        color="brandBlue"
                        size="md"
                    />
                </div>

                <div v-else-if="bet.status === FantasySelectionBetStatus.Lose" class="info-badges">
                    <Badge
                        v-bind:text="i18n.t('fantasy_selection_ranking') + ': ' + bet.myRank + '/' + bet.pool.maxEntries"
                        variant="light"
                        color="brandBlue"
                        size="md"
                    />
                </div>

                <div v-else-if="bet.status === FantasySelectionBetStatus.Refund" class="info-badges">
                    <Badge
                        v-bind:text="i18n.t('fantasy_selection_entries') + ': ' + entriesDisplay"
                        variant="light"
                        color="brandBlue"
                        size="md"
                    />
                </div>
            </div>
        </VTableCell>

        <VTableCell align="right">
            <div class="amount-cell">
                <div class="main-amount">
                    {{ stakeDisplay }}
                </div>
                <div class="sub-amount">
                    {{ stakeDisplay }}
                </div>
            </div>
        </VTableCell>

        <VTableCell align="right">
            <div class="amount-cell">
                <div
                    class="main-amount"
                    v-bind:class="{
                        positive: bet.winLost >= 0,
                        negative: bet.winLost < 0,
                    }"
                >
                    {{ winLostDisplay }}
                </div>
                <div class="sub-amount">
                    {{ playerCommissionDisplay }}
                </div>
            </div>
        </VTableCell>

        <VTableCell align="center">
            <Badge
                type="status"
                variant="solid"
                v-bind:color="statusColorMapping[bet.status]"
                v-bind:text="status"
                size="sm"
            />
        </VTableCell>
    </VTableRow>
</template>

<script lang="ts" setup>
    import { toRefs } from 'vue';
    import Badge from '@/components/common/badge/Badge.vue';
    import VTableCell from '@/components/common/table/VTableCell.vue';
    import VTableRow from '@/components/common/table/VTableRow.vue';
    import TeamBadge from '@/components/fantasySelection/TeamBadge.vue';
    import { useMyBets } from '@/components/fantasySelection/myBets/useMyBets';
    import { i18n } from '@/core/lib/i18n';
    import type { IBet } from '@/interface/IFantasySelectionMyBets';
    import { FantasySelectionBetStatus } from '@/interface/enum';

    const props = defineProps<{
        bet: IBet;
        index: number;
    }>();

    const { bet } = toRefs(props);

    const {
        leagueNames,
        stakeDisplay,
        winLostDisplay,
        playerCommissionDisplay,
        betDate,
        betTime,
        statusColorMapping,
        status,
        prizePoolDisplay,
        entriesDisplay,
    } = useMyBets(bet);
</script>

<style lang="scss">
    @import "@/components/fantasySelection/myBets/BetsTable";
</style>
